// @ts-nocheck -- verbatim TAPP payroll UI compatibility boundary
export type PayrollWorkflowStageId =
  | "prepare"
  | "pohoda"
  | "payslips"
  | "reconcile"
  | "close";

export type PayrollWorkflowStageState =
  | "not_started"
  | "in_progress"
  | "blocked"
  | "completed";

export type PayrollWorkflowStage = {
  id: PayrollWorkflowStageId;
  title: string;
  shortTitle: string;
  state: PayrollWorkflowStageState;
  summary: string;
  nextAction: string;
  blocker?: string;
  interactive: boolean;
  requirementsComplete: boolean;
  canAdvance: boolean;
};

export type AuthoritativePayrollWorkflowStage = {
  id: PayrollWorkflowStageId;
  status: "completed" | "current" | "blocked";
  interactive: boolean;
  requirementsComplete: boolean;
  canAdvance: boolean;
  blocker?: string;
};

export type PayrollPayslipProgress = {
  eligibleCount: number;
  confirmedCount: number;
  missingCount: number;
};

type PayslipDiscardCandidate = {
  id: number;
  employee: { id: number } | null;
  calculationReady: boolean;
};

export const payslipDiscardRequiresReplacement = (
  record: PayslipDiscardCandidate,
  records: PayslipDiscardCandidate[],
  employeeEligible: boolean | undefined,
): boolean | undefined => {
  const lastConfirmedSource =
    record.calculationReady &&
    record.employee !== null &&
    !records.some(
      (candidate) =>
        candidate.id !== record.id &&
        candidate.employee?.id === record.employee?.id &&
        candidate.calculationReady,
    );
  return lastConfirmedSource ? employeeEligible : false;
};

export const payslipDiscardConfirmationDescription = (
  requiresReplacement: boolean | undefined,
) =>
  requiresReplacement === undefined
    ? "Work Profile eligibility could not be verified. Discarding may return the workflow to Review payslips and require a replacement PDF. The record and audit note remain in history."
    : requiresReplacement
      ? "It will no longer be used for payroll. Review payslips becomes current again, and this employee will require a replacement PDF. The record and audit note remain in history."
      : "It will be removed from active payroll review without changing the current workflow. The record and audit note remain in history.";

type AuthoritativePayrollProfile = {
  isActive?: boolean;
  payrollEligibleForMonth?: boolean;
  payrollEligibilityMonth?: string;
};

export const authoritativePayrollUsersForMonth = <
  T extends AuthoritativePayrollProfile,
>(
  users: T[],
  month: string,
) =>
  users.filter(
    (user) =>
      user.isActive === true &&
      user.payrollEligibilityMonth === month &&
      user.payrollEligibleForMonth === true,
  );

type PayslipActionBatch = { id: number };
type PayslipActionRecord = {
  batchId: number;
  matchStatus: string;
  duplicateOfRecordId: number | null;
};

type PayslipDuplicateRecord = {
  month: string;
  duplicateSource: { month: string } | null;
  crossMonthRecoveryAvailable: boolean;
};

export type PayslipDuplicateDisposition =
  | "same-month"
  | "recoverable-cross-month"
  | "protected-cross-month"
  | "unknown";

export const payslipDuplicateDisposition = (
  record: PayslipDuplicateRecord,
): PayslipDuplicateDisposition => {
  const sourceMonth = record.duplicateSource?.month;
  if (!sourceMonth) return "unknown";
  if (sourceMonth === record.month) return "same-month";
  return record.crossMonthRecoveryAvailable
    ? "recoverable-cross-month"
    : "protected-cross-month";
};

export const selectLatestPayslipActionBatch = <
  TBatch extends PayslipActionBatch,
>(
  batches: TBatch[],
  records: PayslipActionRecord[],
) => {
  const actionableBatchIds = new Set(
    records
      .filter(
        (record) =>
          record.matchStatus === "duplicate" &&
          record.duplicateOfRecordId !== null,
      )
      .map((record) => record.batchId),
  );
  return batches.find((batch) => actionableBatchIds.has(batch.id)) ?? null;
};

export const payslipConfirmationSummary = ({
  readyCount,
  attentionCount,
  recoveryCount = 0,
  progressVerified,
  progress,
}: {
  readyCount: number;
  attentionCount: number;
  recoveryCount?: number;
  progressVerified: boolean;
  progress: PayrollPayslipProgress;
}) => {
  if (!progressVerified) {
    return {
      complete: false,
      attention: true,
      title: "Verifying payslip progress",
      description:
        "Waiting for the authoritative selected-month confirmation status.",
    };
  }
  if (readyCount > 0) {
    return {
      complete: false,
      attention: false,
      title: `${readyCount} ${readyCount === 1 ? "payslip is" : "payslips are"} ready`,
      description: `Passed all checks${attentionCount > 0 ? ` · ${attentionCount} excluded` : ""}`,
    };
  }
  if (recoveryCount > 0) {
    return {
      complete: false,
      attention: true,
      title: `${recoveryCount} ${recoveryCount === 1 ? "payslip is" : "payslips are"} stored under the wrong month`,
      description: "Use the recovery action to move them into this review.",
    };
  }
  if (progress.missingCount > 0) {
    return {
      complete: false,
      attention: true,
      title: `${progress.missingCount} ${progress.missingCount === 1 ? "employee still needs" : "employees still need"} a payslip or eligibility fix`,
      description:
        "Upload the missing PDF or correct the employee's Work Profile for this month.",
    };
  }
  if (attentionCount > 0) {
    return {
      complete: false,
      attention: true,
      title: `${attentionCount} ${attentionCount === 1 ? "record needs" : "records need"} attention`,
      description: "Resolve these records individually.",
    };
  }
  const complete =
    progress.eligibleCount > 0 &&
    progress.confirmedCount === progress.eligibleCount;
  return complete
    ? {
        complete: true,
        attention: false,
        title: "All eligible payslips confirmed",
        description: "Required payslips for this month are confirmed.",
      }
    : {
        complete: false,
        attention: true,
        title: "No eligible employees are available",
        description: "Check the selected month's Work Profiles.",
      };
};

const parseAuthoritativePayslipProgress = (
  value: unknown,
): PayrollPayslipProgress | null => {
  if (!value || typeof value !== "object") return null;
  const progress = value as Record<string, unknown>;
  const eligibleCount = progress.eligibleCount;
  const confirmedCount = progress.confirmedCount;
  const missingEmployeeIds = progress.missingEmployeeIds;
  if (
    typeof eligibleCount !== "number" ||
    typeof confirmedCount !== "number" ||
    !Number.isInteger(eligibleCount) ||
    !Number.isInteger(confirmedCount) ||
    !Array.isArray(missingEmployeeIds) ||
    !missingEmployeeIds.every(Number.isInteger) ||
    eligibleCount < 0 ||
    confirmedCount < 0 ||
    confirmedCount > eligibleCount ||
    missingEmployeeIds.length !== eligibleCount - confirmedCount
  )
    return null;
  return {
    eligibleCount,
    confirmedCount,
    missingCount: missingEmployeeIds.length,
  };
};

export const hasAuthoritativePayslipProgress = (value: unknown) =>
  parseAuthoritativePayslipProgress(value) !== null;

export const resolveAuthoritativePayslipProgress = (
  value: unknown,
  fallback: PayrollPayslipProgress,
): PayrollPayslipProgress => {
  return parseAuthoritativePayslipProgress(value) ?? fallback;
};

export const completePrepareStageAfterTimesheetLock = <
  T extends { stages?: AuthoritativePayrollWorkflowStage[] },
>(
  workflow: T | undefined,
  allEligibleTimesheetsLocked: boolean,
): T | undefined => {
  if (!workflow?.stages || !allEligibleTimesheetsLocked) return workflow;
  const prepareIndex = workflow.stages.findIndex(
    (stage) => stage.id === "prepare",
  );
  if (
    prepareIndex < 0 ||
    workflow.stages[prepareIndex]?.status === "completed"
  ) {
    return workflow;
  }
  const nextIndex = workflow.stages.findIndex(
    (stage, index) => index > prepareIndex && stage.status !== "completed",
  );
  return {
    ...workflow,
    stages: workflow.stages.map((stage, index) => {
      if (index === prepareIndex) {
        return {
          ...stage,
          status: "completed",
          interactive: true,
          requirementsComplete: true,
          canAdvance: false,
          blocker: undefined,
        };
      }
      if (index === nextIndex) {
        return {
          ...stage,
          status: "current",
          interactive: true,
          blocker: undefined,
        };
      }
      return stage;
    }),
  };
};

export type PayrollWorkflowInput = {
  employeeCount: number;
  lockedCount: number;
  readyForPohodaCount: number;
  pohodaExported: boolean;
  payslipRecordCount: number;
  confirmedPayslipCount: number;
  blockingPayslipCount: number;
  payslipAttentionDetails?: string[];
  readyToConfirmPayslipCount?: number;
  payslipProcessing: boolean;
  closed?: boolean;
  calculationRun: {
    status: string;
    employeeCount: number;
    redCount: number;
    approvedCount: number;
  } | null;
};

type PayrollWorkflowRefresh = (
  queryKey: readonly unknown[],
  exact: boolean,
) => Promise<unknown>;

export const payrollWorkflowSourceQueries = (month: string) => [
  {
    queryKey: ["openproject", "payroll-profiles", month] as const,
    exact: true,
  },
  {
    queryKey: ["openproject", "timesheet-versions", month] as const,
    exact: false,
  },
  { queryKey: ["openproject", "pohoda-preview", month] as const, exact: true },
  { queryKey: ["openproject", "pohoda-exports", month] as const, exact: true },
  { queryKey: ["openproject", "payslips", month] as const, exact: true },
  { queryKey: ["openproject", "balances", month] as const, exact: true },
];

export const refreshPayrollWorkflowQueries = async ({
  month,
  refetch,
  invalidate,
}: {
  month: string;
  refetch: PayrollWorkflowRefresh;
  invalidate: PayrollWorkflowRefresh;
}) => {
  await Promise.all(
    payrollWorkflowSourceQueries(month).map(({ queryKey, exact }) =>
      refetch(queryKey, exact),
    ),
  );
  await refetch(["openproject", "payroll-workflow", month], true);
  await Promise.all([
    invalidate(["openproject", "balances", "period-summary"], false),
    invalidate(["openproject", "payroll-audit"], false),
  ]);
};

export const createInitialBalanceCalculationGuard = () => {
  type CalculationState =
    | { status: "in_flight"; error: null }
    | { status: "failed"; error: string };
  const monthStates = new Map<string, CalculationState>();
  return {
    claim({
      month,
      ready,
      hasCurrentRun,
    }: {
      month: string;
      ready: boolean;
      hasCurrentRun: boolean;
    }) {
      if (!ready || hasCurrentRun || monthStates.has(month)) return false;
      monthStates.set(month, { status: "in_flight", error: null });
      return true;
    },
    retry(month: string) {
      if (monthStates.get(month)?.status === "in_flight") return false;
      monthStates.set(month, { status: "in_flight", error: null });
      return true;
    },
    fail(month: string, error: string) {
      monthStates.set(month, {
        status: "failed",
        error: error.trim() || "Balance calculation failed.",
      });
    },
    complete(month: string) {
      monthStates.delete(month);
    },
    release(month: string) {
      if (monthStates.get(month)?.status === "in_flight")
        monthStates.delete(month);
    },
    state: (month: string) =>
      monthStates.get(month) ?? { status: "idle" as const, error: null },
  };
};

const stageState = (
  completed: boolean,
  started: boolean,
  blocked = false,
): PayrollWorkflowStageState =>
  completed
    ? "completed"
    : blocked
      ? "blocked"
      : started
        ? "in_progress"
        : "not_started";

export const derivePayrollWorkflow = (
  input: PayrollWorkflowInput,
): PayrollWorkflowStage[] => {
  const prepared =
    input.employeeCount > 0 && input.lockedCount >= input.employeeCount;
  const pohodaCreated = prepared && input.pohodaExported;
  const payslipsReviewed =
    pohodaCreated &&
    input.employeeCount > 0 &&
    input.confirmedPayslipCount >= input.employeeCount &&
    input.blockingPayslipCount === 0;
  const reconciled =
    payslipsReviewed &&
    Boolean(input.calculationRun) &&
    input.calculationRun!.redCount === 0;
  const approved =
    reconciled &&
    Boolean(
      input.calculationRun &&
      input.calculationRun.status === "approved" &&
      input.calculationRun.approvedCount >= input.calculationRun.employeeCount,
    );
  const completed = [
    prepared,
    pohodaCreated,
    payslipsReviewed,
    approved,
    Boolean(input.closed && approved),
  ];
  const firstIncomplete = Math.max(
    0,
    completed.findIndex((value) => !value),
  );
  const interactive = (index: number) =>
    completed[index] || index === firstIncomplete;
  const blockedBy = (index: number) =>
    index > firstIncomplete
      ? `Complete ${["Prepare records", "Pohoda input", "Review payslips", "Check balances & approve"][index - 1]} first.`
      : undefined;
  const attentionSummary =
    input.blockingPayslipCount > 0
      ? ` ${input.blockingPayslipCount} need attention.`
      : "";
  const readyToConfirmSummary = input.readyToConfirmPayslipCount
    ? ` ${input.readyToConfirmPayslipCount} ${input.readyToConfirmPayslipCount === 1 ? "payslip is" : "payslips are"} ready to confirm.`
    : "";
  const reconciliationChecks = input.calculationRun?.redCount ?? 0;
  const reconcileSummary = (() => {
    if (reconciliationChecks === 0) return null;
    return `Balance calculation already ran. ${reconciliationChecks} mismatch${reconciliationChecks === 1 ? "" : "es"} need review before approval.`;
  })();
  const reconcileBlockageHint = reconciliationChecks
    ? `Fix or confirm every mismatch in Calculation details.`
    : "";

  return [
    {
      id: "prepare",
      title: "Prepare records",
      shortTitle: "Prepare records",
      state: stageState(
        prepared,
        input.lockedCount > 0,
        input.employeeCount === 0,
      ),
      summary:
        input.employeeCount === 0
          ? "No eligible Work Profiles are configured for this month."
          : `${input.lockedCount} of ${input.employeeCount} eligible timesheets are locked.`,
      nextAction: prepared
        ? "Review locked records"
        : "Validate Work Profiles and lock every timesheet",
      blocker:
        input.employeeCount === 0
          ? "Add or correct the Work Profiles that apply to this month."
          : undefined,
      interactive: interactive(0),
      requirementsComplete: prepared,
      canAdvance: prepared,
    },
    {
      id: "pohoda",
      title: "Pohoda input",
      shortTitle: "Pohoda input",
      state: stageState(pohodaCreated, prepared, !prepared),
      summary: pohodaCreated
        ? "The current locked records have a Pohoda input."
        : `${input.readyForPohodaCount} of ${input.employeeCount} employees are ready for export.`,
      nextAction: pohodaCreated
        ? "Review the current XLSX"
        : "Generate and verify the XLSX",
      blocker: blockedBy(1),
      interactive: interactive(1),
      requirementsComplete: pohodaCreated,
      canAdvance: pohodaCreated,
    },
    {
      id: "payslips",
      title: "Review payslips",
      shortTitle: "Review payslips",
      state: stageState(
        payslipsReviewed,
        input.payslipRecordCount > 0 || input.payslipProcessing,
        !pohodaCreated,
      ),
      summary: input.payslipProcessing
        ? "Processing the uploaded payslips. You can keep this page open."
        : `${input.confirmedPayslipCount} of ${input.employeeCount} payslips confirmed.${attentionSummary}${readyToConfirmSummary}`,
      nextAction: payslipsReviewed
        ? "Review confirmed payslips"
        : "Upload, correct, and confirm every payslip",
      blocker: blockedBy(2),
      interactive: interactive(2),
      requirementsComplete: payslipsReviewed,
      canAdvance: payslipsReviewed,
    },
    {
      id: "reconcile",
      title: "Check balances & approve",
      shortTitle: "Balances & approval",
      state: stageState(
        approved,
        Boolean(input.calculationRun),
        !payslipsReviewed,
      ),
      summary: input.calculationRun
        ? input.calculationRun.redCount
          ? (reconcileSummary ?? reconcileBlockageHint)
          : approved
            ? `All ${input.calculationRun.employeeCount} employee balances are approved.`
            : `All ${input.calculationRun.employeeCount} employee balances are ready for approval.`
        : "The first balance check starts automatically after every payslip is confirmed.",
      nextAction: approved
        ? "Move to Close & email payroll"
        : reconciled
          ? "Approve the current payroll"
          : input.calculationRun?.redCount
            ? `You need to fix or confirm ${input.calculationRun.redCount} remaining mismatch${input.calculationRun.redCount === 1 ? "" : "es"} to continue.`
            : "Go to Calculation details.",
      blocker: blockedBy(3),
      interactive: interactive(3),
      requirementsComplete: approved,
      canAdvance: approved,
    },
    {
      id: "close",
      title: "Close & email payroll",
      shortTitle: "Close & email payroll",
      state: stageState(Boolean(input.closed && approved), approved, !approved),
      summary: input.closed
        ? "The month and its checksummed artifacts are published to Period Summary."
        : "Review final artifacts and record the month closure.",
      nextAction: input.closed
        ? "View Period Summary"
        : "Close and publish the month",
      blocker: blockedBy(4),
      interactive: interactive(4),
      requirementsComplete: Boolean(input.closed && approved),
      canAdvance: false,
    },
  ];
};

export const recommendedPayrollStage = (stages: PayrollWorkflowStage[]) =>
  stages.find((stage) => stage.state !== "completed") ?? stages.at(-1)!;

export const resolveActivePayrollWorkflowStageId = ({
  stages,
  selectedId,
  recommendedId,
}: {
  stages: PayrollWorkflowStage[];
  selectedId: PayrollWorkflowStageId | null;
  recommendedId: PayrollWorkflowStageId;
}): PayrollWorkflowStageId => {
  const selectedStage = selectedId
    ? stages.find((stage) => stage.id === selectedId)
    : null;
  return selectedStage?.id ?? recommendedId;
};

export const shouldResetPayrollWorkflowSelection = (
  previousMonth: string,
  currentMonth: string,
) => previousMonth !== currentMonth;

export const nextPayrollWorkflowStage = (
  stages: PayrollWorkflowStage[],
  currentId: PayrollWorkflowStageId,
) => {
  const currentIndex = stages.findIndex((stage) => stage.id === currentId);
  return currentIndex >= 0 ? (stages[currentIndex + 1] ?? null) : null;
};

export const payrollNextLabel = (
  stages: PayrollWorkflowStage[],
  currentId: PayrollWorkflowStageId,
  pending = false,
) => {
  const nextStage = nextPayrollWorkflowStage(stages, currentId);
  if (!nextStage) return pending ? "Moving…" : "Next";
  return pending ? "Moving…" : "Next";
};

export const payrollNextDisabledReason = (
  stage: PayrollWorkflowStage,
  refreshing: boolean,
) => {
  if (refreshing) return "Refreshing the authoritative payroll state…";
  if (stage.canAdvance) return null;
  return stage.blocker?.trim() || stage.nextAction;
};

export const applyAuthoritativePayrollWorkflow = (
  displayStages: PayrollWorkflowStage[],
  authoritativeStages: AuthoritativePayrollWorkflowStage[],
) =>
  displayStages.map((stage) => {
    const authoritative = authoritativeStages.find(
      (entry) => entry.id === stage.id,
    );
    if (!authoritative) {
      return {
        ...stage,
        state: "blocked" as const,
        interactive: false,
        blocker: "Workflow state is incomplete. Retry before continuing.",
      };
    }
    const authoritativeState =
      authoritative.status === "completed"
        ? ("completed" as const)
        : authoritative.status === "current"
          ? ("in_progress" as const)
          : ("blocked" as const);
    const stateDisagrees = stage.state !== authoritativeState;
    return {
      ...stage,
      state: authoritativeState,
      summary: stateDisagrees
        ? authoritative.status === "completed"
          ? "The server has verified this stage against the current payroll inputs."
          : authoritative.status === "current"
            ? "The server has confirmed this as the current payroll stage."
            : `The server requires ${stage.title} to be completed before later work can continue.`
        : stage.summary,
      interactive: authoritative.interactive,
      requirementsComplete: authoritative.requirementsComplete,
      canAdvance: authoritative.canAdvance,
      blocker:
        authoritative.blocker ??
        (stateDisagrees && authoritative.status !== "blocked"
          ? undefined
          : stage.blocker),
    };
  });

export const guardPayrollWorkflowAgainstPayslipMismatch = (
  stages: PayrollWorkflowStage[],
  payslipListContradictsWorkflow: boolean,
) => {
  if (!payslipListContradictsWorkflow) return stages;
  const payslipIndex = stages.findIndex((stage) => stage.id === "payslips");
  if (payslipIndex < 0) return stages;
  return stages.map((stage, index) => {
    if (index < payslipIndex) return stage;
    if (index === payslipIndex) {
      return {
        ...stage,
        state: "in_progress" as const,
        summary:
          "The employee list still contains a missing or unconfirmed payslip. Resolve that record before continuing.",
        blocker: undefined,
        interactive: true,
        requirementsComplete: false,
        canAdvance: false,
      };
    }
    return {
      ...stage,
      state: "blocked" as const,
      blocker: "Resolve every missing or unconfirmed payslip first.",
      interactive: false,
      requirementsComplete: false,
      canAdvance: false,
    };
  });
};

export const payrollStageStateLabel = (value: PayrollWorkflowStageState) => {
  if (value === "not_started") return "Not started";
  if (value === "in_progress") return "Current";
  if (value === "blocked") return "Blocked";
  return "Completed";
};
